import { Layers, X, Map as MapIcon, Satellite, Mountain, Contrast, Eye, EyeOff } from "lucide-react";
import { Switch } from "@/components/ui/switch";

const BASE_OPTIONS = [
  { key: "standard", label: "Standard", Icon: MapIcon },
  { key: "satellite", label: "Satellit", Icon: Satellite },
  { key: "topo", label: "Topografie", Icon: Mountain },
  { key: "light", label: "Hell / Grau", Icon: Contrast },
];

// Layer control: pick the OpenStreetMap base map + toggle the PDF overlay
// ("Overlay") on/off with its own opacity slider.
export default function LayersMenu({ open, onClose, baseLayer, setBaseLayer, ov }) {
  if (!open) return null;
  const { overlay, visible, toggleVisible, opacity, setOpacity, persistProps } = ov;

  return (
    <div
      className="pointer-events-auto absolute bottom-[4.75rem] left-3 z-[720] w-64 max-w-[85vw] rounded-2xl bg-white/95 p-4 shadow-xl ring-1 ring-slate-200 backdrop-blur"
      data-testid="layers-panel"
    >
      <div className="mb-3 flex items-center justify-between">
        <span className="flex items-center gap-2 font-display text-sm font-bold text-slate-900">
          <Layers size={16} /> Ebenen
        </span>
        <button
          onClick={onClose}
          data-testid="layers-panel-close"
          aria-label="Schließen"
          className="grid h-7 w-7 place-items-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-800"
        >
          <X size={16} />
        </button>
      </div>

      <p className="mb-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-400">Basiskarte</p>
      <div className="flex flex-col gap-1.5">
        {BASE_OPTIONS.map(({ key, label, Icon }) => {
          const active = baseLayer === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setBaseLayer(key)}
              data-testid={`layers-base-${key}`}
              aria-pressed={active}
              className={`flex items-center gap-2.5 rounded-xl px-3 py-2 text-sm font-semibold transition-colors ${
                active
                  ? "bg-slate-900 text-white"
                  : "bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50"
              }`}
            >
              <Icon size={16} className={active ? "text-white" : "text-slate-500"} />
              {label}
              {active && <span className="ml-auto text-[10px] font-bold uppercase tracking-widest">aktiv</span>}
            </button>
          );
        })}
      </div>

      {overlay && (
        <div className="mt-3 border-t border-slate-100 pt-3">
          <div className="flex items-center justify-between gap-2">
            <span className="flex min-w-0 items-center gap-2 text-sm font-semibold text-slate-800">
              {visible ? (
                <Eye size={15} className="shrink-0 text-slate-500" />
              ) : (
                <EyeOff size={15} className="shrink-0 text-slate-400" />
              )}
              <span className="truncate" title={overlay.name}>
                Overlay
              </span>
            </span>
            <Switch
              checked={visible}
              onCheckedChange={toggleVisible}
              data-testid="layers-overlay-toggle"
              aria-label="Overlay ein-/ausblenden"
            />
          </div>
          <div className="mt-2">
            <input
              type="range"
              min="0.1"
              max="1"
              step="0.05"
              value={opacity}
              onChange={(e) => setOpacity(parseFloat(e.target.value))}
              onPointerUp={() => persistProps({ opacity })}
              disabled={!visible}
              data-testid="layers-overlay-opacity"
              className="w-full accent-slate-900 disabled:opacity-40"
            />
            <p className="text-[10px] uppercase tracking-widest text-slate-400">
              Deckkraft {Math.round(opacity * 100)}%
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
